import { NavLink } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuth } from '@/stores/authStore'
import { useJobOfferNotifications } from '@/hooks/useJobOfferNotifications'
import { useLanguage, type Language } from '@/stores/languageStore'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  Search,
  Sparkles,
  Briefcase,
  FileText,
  Users,
  CreditCard,
  History,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  User,
} from 'lucide-react'

const COLLAPSED_KEY = 'sidebar_collapsed'

export default function Sidebar() {
  const { user, logout } = useAuth()
  const { unseenCount } = useJobOfferNotifications()
  const { language, setLanguage } = useLanguage()
  const { t } = useTranslation(['common', 'auth'])
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem(COLLAPSED_KEY) === 'true'
  })

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, String(collapsed))
  }, [collapsed])

  const LANGUAGES: { code: Language; label: string }[] = [
    { code: 'en', label: 'EN' },
    { code: 'fr', label: 'FR' },
    { code: 'ar', label: 'AR' },
  ]

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm font-medium ${
      collapsed ? 'justify-center' : ''
    } ${isActive ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`

  const aiNavLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm font-semibold ${
      collapsed ? 'justify-center' : ''
    } ${
      isActive
        ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40'
        : 'bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-300 hover:from-blue-600/30 hover:to-purple-600/30'
    }`

  return (
    <aside
      className={`flex flex-col bg-gray-900 border-r border-gray-800 h-screen sticky top-0 flex-shrink-0 transition-all duration-200 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo + collapse toggle */}
      <div
        className={`flex items-center h-16 border-b border-gray-800 px-3 flex-shrink-0 ${
          collapsed ? 'justify-center' : 'justify-between'
        }`}
      >
        {!collapsed && (
          <span className="text-lg font-extrabold tracking-tight text-white truncate select-none">
            headhunter<span className="text-blue-400">.tn</span>
          </span>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* Navigation links */}
      <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
        {user?.role === 'CANDIDATE' && (
          <>
            <NavLink
              to="/candidate/dashboard"
              className={navLinkClass}
              title={collapsed ? t('nav.dashboard') : undefined}
            >
              <LayoutDashboard size={20} className="flex-shrink-0" />
              {!collapsed && <span>{t('nav.dashboard')}</span>}
            </NavLink>

            <NavLink
              to="/candidate/job-offers"
              className={navLinkClass}
              title={collapsed ? t('nav.jobOffers') : undefined}
            >
              <span className="relative flex-shrink-0">
                <FileText size={20} />
                {unseenCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-[1rem] flex items-center justify-center px-0.5 leading-none">
                    {unseenCount > 99 ? '99+' : unseenCount}
                  </span>
                )}
              </span>
              {!collapsed && (
                <span className="flex items-center gap-2 flex-1">
                  {t('nav.jobOffers')}
                  {unseenCount > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-[1rem] flex items-center justify-center px-1 leading-none">
                      {unseenCount > 99 ? '99+' : unseenCount}
                    </span>
                  )}
                </span>
              )}
            </NavLink>

            <NavLink
              to="/candidate/profile/edit"
              className={navLinkClass}
              title={collapsed ? t('nav.profile') : undefined}
            >
              <User size={20} className="flex-shrink-0" />
              {!collapsed && <span>{t('nav.profile')}</span>}
            </NavLink>
          </>
        )}

        {user?.role === 'RECRUITER' && (
          <>
            <NavLink
              to="/recruiter/dashboard"
              className={navLinkClass}
              title={collapsed ? t('nav.dashboard') : undefined}
            >
              <LayoutDashboard size={20} className="flex-shrink-0" />
              {!collapsed && <span>{t('nav.dashboard')}</span>}
            </NavLink>

            <NavLink
              to="/recruiter/search"
              className={navLinkClass}
              title={collapsed ? t('nav.search') : undefined}
            >
              <Search size={20} className="flex-shrink-0" />
              {!collapsed && <span>{t('nav.search')}</span>}
            </NavLink>

            <NavLink
              to="/recruiter/ai-matching"
              className={aiNavLinkClass}
              title={collapsed ? t('nav.aiMatch') : undefined}
            >
              <Sparkles size={20} className="flex-shrink-0" />
              {!collapsed && <span>{t('nav.aiMatch')}</span>}
            </NavLink>

            <NavLink
              to="/recruiter/jobs"
              className={navLinkClass}
              title={collapsed ? t('nav.jobs') : undefined}
            >
              <Briefcase size={20} className="flex-shrink-0" />
              {!collapsed && <span>{t('nav.jobs')}</span>}
            </NavLink>

            <NavLink
              to="/recruiter/job-offers"
              className={navLinkClass}
              title={collapsed ? t('nav.offers') : undefined}
            >
              <FileText size={20} className="flex-shrink-0" />
              {!collapsed && <span>{t('nav.offers')}</span>}
            </NavLink>

            <NavLink
              to="/recruiter/contacts"
              className={navLinkClass}
              title={collapsed ? t('nav.contacts') : undefined}
            >
              <Users size={20} className="flex-shrink-0" />
              {!collapsed && <span>{t('nav.contacts')}</span>}
            </NavLink>

            <div className="my-2 border-t border-gray-800" />

            <NavLink
              to="/recruiter/packs"
              className={navLinkClass}
              title={collapsed ? t('nav.subscription') : undefined}
            >
              <CreditCard size={20} className="flex-shrink-0" />
              {!collapsed && <span>{t('nav.subscription')}</span>}
            </NavLink>

            <NavLink
              to="/recruiter/pack-history"
              className={navLinkClass}
              title={collapsed ? t('nav.packHistory') : undefined}
            >
              <History size={20} className="flex-shrink-0" />
              {!collapsed && <span>{t('nav.packHistory')}</span>}
            </NavLink>

            <NavLink
              to="/recruiter/profile/edit"
              className={navLinkClass}
              title={collapsed ? t('nav.profile') : undefined}
            >
              <Settings size={20} className="flex-shrink-0" />
              {!collapsed && <span>{t('nav.profile')}</span>}
            </NavLink>
          </>
        )}
      </nav>

      {/* Bottom: language switcher + user + logout */}
      <div className="border-t border-gray-800 p-2 space-y-2 flex-shrink-0">
        {/* Language switcher (only when expanded) */}
        {!collapsed && (
          <div className="flex items-center gap-1 px-2 py-1">
            {LANGUAGES.map(({ code, label }) => (
              <button
                key={code}
                onClick={() => setLanguage(code)}
                className={`text-xs font-medium px-2 py-1 rounded transition-colors ${
                  language === code
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-500 hover:text-white hover:bg-white/10'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* User avatar + name */}
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          {user?.profilePhoto ? (
            <img
              src={user.profilePhoto}
              alt={user.firstName || 'User'}
              className="w-7 h-7 rounded-full rounded-avatar border border-gray-700 object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-7 h-7 rounded-full rounded-avatar bg-gray-700 flex items-center justify-center flex-shrink-0">
              <User size={15} className="text-gray-400" />
            </div>
          )}
          {!collapsed && (
            <span className="text-sm text-gray-300 truncate font-medium flex-1 min-w-0">
              {user?.firstName || user?.email}
            </span>
          )}
        </div>

        {/* Logout */}
        <button
          onClick={logout}
          title={collapsed ? t('button.signOut') : undefined}
          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors w-full ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <LogOut size={18} className="flex-shrink-0" />
          {!collapsed && <span>{t('button.signOut')}</span>}
        </button>
      </div>
    </aside>
  )
}
